import {Dialog, DialogBackdrop, DialogPanel, DialogTitle, Switch} from "@headlessui/react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTimes} from "@fortawesome/free-solid-svg-icons";
import {PrimaryButton} from "../../Buttons/PrimaryButton";
import * as React from "react";
import {useTranslation} from "react-i18next";
import * as yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {useBoundStore} from "../../../Store/store";
import toast from "react-hot-toast";
import {SelectInput} from "../../Form/SelectInput";
import {TextInput} from "../../Form/TextInput";
import {useEffect, useState} from "react";
import {NumberInput} from "../../Form/NumberInput";
import {TextAreaInput} from "../../Form/TextAreaInput";
import {isMyCcpSubdomain} from "../../../Utils/SubdomainHelper";

export const UpdateBranchDialog = (props) => {
    const {
        open,
        onClose,
        branch,
    } = props;

    const {t} = useTranslation();

    const updateBranch = useBoundStore((state) => state.updateBranch);
    const getAllContractTypes = useBoundStore((state) => state.getAllContractTypes);
    const contractTypes = useBoundStore((state) => state.contractTypes);
    const user = useBoundStore((state) => state.user);

    const [hasInvoiceAddress, setHasInvoiceAddress] = useState(false);
    const [hasContractsDestination, setHasContractsDestination] = useState(false);
    const [hasPerformanceStatementDestination, setHasPerformanceStatementDestination] = useState(false);

    useEffect(() => {
        getAllContractTypes();
    }, [getAllContractTypes])

    useEffect(() => {
        if (branch?.invoice_email || branch?.invoice_company_name || branch?.invoice_vat_number) {
            setHasInvoiceAddress(true);
        } else {
            setHasInvoiceAddress(false);
        }

        if (branch?.contracts_email || branch?.contracts_firstname || branch?.contracts_lastname) {
            setHasContractsDestination(true);
        } else {
            setHasContractsDestination(false);
        }

        if (branch?.performancestatement_email || branch?.performancestatement_firstname || branch?.performancestatement_lastname) {
            setHasPerformanceStatementDestination(true);
        } else {
            setHasPerformanceStatementDestination(false);
        }
    }, [branch]);

    const schema = yup.object({
        contact_firstname: yup.string().required().max(255),
        contact_lastname: yup.string().required().max(255),
        contact_email: yup.string().required().email().max(255),
        contact_tel: yup.string().required().max(255),
        vat_number: yup.string().required().max(255),
        company_name: yup.string().required().max(255),
        bank_account: yup.string().nullable().max(255),
        street: yup.string().required().max(255),
        number: yup.string().required().max(255),
        bus: yup.string().nullable().max(255),
        postal: yup.string().required().max(255),
        city: yup.string().required().max(255),
        country: yup.string().required().max(255),
        province: yup.string()
            .required()
            .oneOf([
                'antwerpen', 'limburg', 'oost-vlaanderen', 'vlaams-brabant', 'west-vlaanderen',
                'henegouwen', 'luik', 'luxemburg', 'namen', 'waals-brabant', 'brussels-hoofdstedelijk-gewest'
            ]),
        contract_type_id: yup.string().nullable(),
        alias: yup.string().nullable(),
        shift_confirmed_message: yup.string().nullable(),
        max_hourly_rate_vk: yup.number().nullable().transform((value, originalValue) => originalValue === '' ? null : value),
        max_hourly_rate_zk: yup.number().nullable().transform((value, originalValue) => originalValue === '' ? null : value),
        invoice_email: hasInvoiceAddress ? yup.string().required().email().max(255) : yup.string().nullable().max(255),
        invoice_company_name: hasInvoiceAddress ? yup.string().required().max(255) : yup.string().nullable().max(255),
        invoice_vat_number: hasInvoiceAddress ? yup.string().required().max(255) : yup.string().nullable().max(255),
        invoice_street: hasInvoiceAddress ? yup.string().required().max(255) : yup.string().nullable().max(255),
        invoice_number: hasInvoiceAddress ? yup.string().required().max(255) : yup.string().nullable().max(255),
        invoice_bus: yup.string().nullable().max(255),
        invoice_city: hasInvoiceAddress ? yup.string().required().max(255) : yup.string().nullable().max(255),
        invoice_postal: hasInvoiceAddress ? yup.string().required().max(255) : yup.string().nullable().max(255),
        contracts_email: hasContractsDestination ? yup.string().required().email().max(255) : yup.string().nullable().max(255),
        contracts_firstname: hasContractsDestination ? yup.string().required().max(255) : yup.string().nullable().max(255),
        contracts_lastname: hasContractsDestination ? yup.string().required().max(255) : yup.string().nullable().max(255),
        performancestatement_email: hasPerformanceStatementDestination ? yup.string().required().email().max(255) : yup.string().nullable().max(255),
        performancestatement_firstname: hasPerformanceStatementDestination ? yup.string().required().max(255) : yup.string().nullable().max(255),
        performancestatement_lastname: hasPerformanceStatementDestination ? yup.string().required().max(255) : yup.string().nullable().max(255),
        relation_code_exact: user?.roles?.includes('admin') ? yup.string().required().max(255) : yup.string().nullable().max(255),
    }).required();

    const {
        register,
        handleSubmit,
        formState: {errors},
        getValues,
        setValue,
        control,
        reset,
        watch,
    } = useForm({
        resolver: yupResolver(schema),
        reValidateMode: 'onBlur',
        mode: 'onChange',
        defaultValues: {
            contact_firstname: branch?.contact_firstname || '',
            contact_lastname: branch?.contact_lastname || '',
            contact_email: branch?.contact_email || '',
            contact_tel: branch?.contact_tel || '',
            vat_number: branch?.vat_number || '',
            company_name: branch?.company_name || '',
            bank_account: branch?.bank_account || '',
            street: branch?.street || '',
            number: branch?.number || '',
            bus: branch?.bus || '',
            postal: branch?.postal || '',
            city: branch?.city || '',
            country: branch?.country || '',
            province: branch?.province || '',
            contract_type_id: branch?.contract_type_id || null,
            max_hourly_rate_vk: branch?.max_hourly_rate_vk || null,
            max_hourly_rate_zk: branch?.max_hourly_rate_zk || null,
            alias: branch?.alias || null,
            shift_confirmed_message: branch?.shift_confirmed_message || null,
            invoice_email: branch?.invoice_email || '',
            invoice_company_name: branch?.invoice_company_name || '',
            invoice_vat_number: branch?.invoice_vat_number || '',
            invoice_street: branch?.invoice_street || '',
            invoice_number: branch?.invoice_number || '',
            invoice_bus: branch?.invoice_bus || '',
            invoice_city: branch?.invoice_city || '',
            invoice_postal: branch?.invoice_postal || '',
            contracts_email: branch?.contracts_email || '',
            contracts_firstname: branch?.contracts_firstname || '',
            contracts_lastname: branch?.contracts_lastname || '',
            performancestatement_email: branch?.performancestatement_email || '',
            performancestatement_firstname: branch?.performancestatement_firstname || '',
            performancestatement_lastname: branch?.performancestatement_lastname || '',
            relation_code_exact: branch?.relation_code_exact || '',
        }
    });

    useEffect(() => {
        reset({
            contact_firstname: branch?.contact_firstname || '',
            contact_lastname: branch?.contact_lastname || '',
            contact_email: branch?.contact_email || '',
            contact_tel: branch?.contact_tel || '',
            vat_number: branch?.vat_number || '',
            company_name: branch?.company_name || '',
            bank_account: branch?.bank_account || '',
            street: branch?.street || '',
            number: branch?.number || '',
            bus: branch?.bus || '',
            postal: branch?.postal || '',
            city: branch?.city || '',
            country: branch?.country || '',
            province: branch?.province || '',
            alias: branch?.alias || null,
            contract_type_id: parseInt(branch?.contract_type_id) || null,
            max_hourly_rate_vk: branch?.max_hourly_rate_vk || null,
            max_hourly_rate_zk: branch?.max_hourly_rate_zk || null,
            shift_confirmed_message: branch?.shift_confirmed_message || null,
            invoice_email: branch?.invoice_email || '',
            invoice_company_name: branch?.invoice_company_name || '',
            invoice_vat_number: branch?.invoice_vat_number || '',
            invoice_street: branch?.invoice_street || '',
            invoice_number: branch?.invoice_number || '',
            invoice_bus: branch?.invoice_bus || '',
            invoice_city: branch?.invoice_city || '',
            invoice_postal: branch?.invoice_postal || '',
            contracts_email: branch?.contracts_email || '',
            contracts_firstname: branch?.contracts_firstname || '',
            contracts_lastname: branch?.contracts_lastname || '',
            performancestatement_email: branch?.performancestatement_email || '',
            performancestatement_firstname: branch?.performancestatement_firstname || '',
            performancestatement_lastname: branch?.performancestatement_lastname || '',
            relation_code_exact: branch?.relation_code_exact || '',
        })
    }, [branch]);

    const onSubmit = (data) => {
        const submitData = hasInvoiceAddress ? data : {
            ...data,
            invoice_email: null,
            invoice_company_name: null,
            invoice_vat_number: null,
            invoice_street: null,
            invoice_number: null,
            invoice_bus: null,
            invoice_city: null,
            invoice_postal: null,
        };

        const finalData = hasContractsDestination ? submitData : {
            ...submitData,
            contracts_email: null,
            contracts_firstname: null,
            contracts_lastname: null,
        };

        const completeData = hasPerformanceStatementDestination ? finalData : {
            ...finalData,
            performancestatement_email: null,
            performancestatement_firstname: null,
            performancestatement_lastname: null,
        };

        updateBranch(branch?.id, completeData)
            .then(() => {
                toast.success(t('branches.toast_updated'));
                onClose();
            })
            .catch((error) => {
                console.error('Error updating vestiging', error);
            });
    };

    return (
        <Dialog className="relative z-50" open={open} onClose={onClose}>
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity duration-500 ease-in-out data-[closed]:opacity-0"
            />

            <div className="fixed inset-0 overflow-y-scroll">
                <div className="absolute inset-0 overflow-y-scroll">
                    <div className="pointer-events-none p-4 rounded-lg fixed inset-y-0 right-0 flex max-w-full">
                        <DialogPanel
                            transition
                            className="pointer-events-auto w-screen max-w-lg transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
                        >
                            <div className="flex h-full rounded-2xl flex-col overflow-y-scroll bg-white py-6 shadow-xl">
                                <div className="px-4 sm:px-6">
                                    <div className="flex items-start justify-between">
                                        <DialogTitle className="text-base font-semibold leading-6 text-gray-900">
                                            {t('branches.update_branch')}
                                        </DialogTitle>
                                        <div className="ml-3 flex h-7 items-center">
                                            <button
                                                type="button"
                                                className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                                                onClick={() => onClose()}
                                            >
                                                <span className="absolute -inset-2.5" />
                                                <span className="sr-only">Close panel</span>
                                                <FontAwesomeIcon icon={faTimes} className="h-6 w-6" aria-hidden="true" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="relative mt-6 flex-1 px-4 sm:px-6">
                                    <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
                                        <div className="mb-2">
                                            <TextInput
                                                register={register}
                                                name={'contact_firstname'}
                                                translationIndex={'general.fields.contact_firstname'}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <TextInput
                                                register={register}
                                                name={'contact_lastname'}
                                                translationIndex={'general.fields.contact_lastname'}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <TextInput
                                                register={register}
                                                name={'contact_email'}
                                                translationIndex={'general.fields.contact_email'}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <TextInput
                                                register={register}
                                                name={'contact_tel'}
                                                translationIndex={'general.fields.contact_tel'}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <TextInput
                                                register={register}
                                                name={'vat_number'}
                                                translationIndex={'general.fields.vat_number'}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <TextInput
                                                register={register}
                                                name={'company_name'}
                                                translationIndex={'general.fields.company_name'}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <TextInput
                                                register={register}
                                                name={'alias'}
                                                translationIndex={t('branches.alias')}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <TextInput
                                                register={register}
                                                name={'bank_account'}
                                                translationIndex={'general.fields.bank_account'}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <TextInput
                                                register={register}
                                                name={'street'}
                                                translationIndex={'general.fields.street'}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <TextInput
                                                register={register}
                                                name={'number'}
                                                translationIndex={'general.fields.number'}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <TextInput
                                                register={register}
                                                name={'bus'}
                                                translationIndex={'general.fields.bus'}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <TextInput
                                                register={register}
                                                name={'postal'}
                                                translationIndex={'general.fields.postal'}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <TextInput
                                                register={register}
                                                name={'city'}
                                                translationIndex={'general.fields.city'}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <TextInput
                                                register={register}
                                                name={'country'}
                                                translationIndex={'general.fields.country'}
                                                errors={errors}
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <SelectInput
                                                name={'province'}
                                                options={[
                                                    { value: 'antwerpen', label: t('provinces.antwerp') },
                                                    { value: 'limburg', label: t('provinces.limburg') },
                                                    { value: 'oost-vlaanderen', label: t('provinces.east_flanders') },
                                                    { value: 'vlaams-brabant', label: t('provinces.flemish_brabant') },
                                                    { value: 'west-vlaanderen', label: t('provinces.west_flanders') },
                                                    { value: 'henegouwen', label: t('provinces.hainaut') },
                                                    { value: 'luik', label: t('provinces.liege') },
                                                    { value: 'luxemburg', label: t('provinces.luxembourg') },
                                                    { value: 'namen', label: t('provinces.namur') },
                                                    { value: 'waals-brabant', label: t('provinces.walloon_brabant') },
                                                    { value: 'brussels-hoofdstedelijk-gewest', label: t('provinces.brussels') },
                                                ]}
                                                setValue={setValue}
                                                control={control}
                                                value={getValues('province') ?? null}
                                                placeholder={t('general.fields.province')}
                                                translationIndex={'general.fields.province'}
                                            />
                                        </div>

                                        <div className={"mb-2"}>
                                            <TextAreaInput
                                                register={register}
                                                name={'shift_confirmed_message'}
                                                translationIndex={t('branches.additional_info') + ' (' + t('branches.additional_info_warning') + ')'}
                                                rows={5}
                                                errors={errors}
                                            />
                                        </div>

                                        <div className="mb-4 p-3 bg-gray-100 rounded-lg border border-gray-200">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-medium text-gray-900">
                                                    {t('branches.has_different_invoice_address')}
                                                </span>
                                                <Switch
                                                    checked={hasInvoiceAddress}
                                                    onChange={setHasInvoiceAddress}
                                                    className={`${
                                                        hasInvoiceAddress ? 'bg-primary-600' : 'bg-gray-300'
                                                    } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
                                                >
                                                    <span
                                                        className={`${
                                                            hasInvoiceAddress ? 'translate-x-6' : 'translate-x-1'
                                                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                                                    />
                                                </Switch>
                                            </div>
                                        </div>

                                        {hasInvoiceAddress && (
                                            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                                <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('branches.invoice_address')}</h3>

                                                <div className="mb-2">
                                                    <TextInput
                                                        register={register}
                                                        name={'invoice_email'}
                                                        translationIndex={t('branches.invoice_email')}
                                                        errors={errors}
                                                    />
                                                </div>
                                                <div className="mb-2">
                                                    <TextInput
                                                        register={register}
                                                        name={'invoice_company_name'}
                                                        translationIndex={t('branches.invoice_company')}
                                                        errors={errors}
                                                    />
                                                </div>
                                                <div className="mb-2">
                                                    <TextInput
                                                        register={register}
                                                        name={'invoice_vat_number'}
                                                        translationIndex={t('branches.invoice_vat')}
                                                        errors={errors}
                                                    />
                                                </div>
                                                <div className="mb-2">
                                                    <TextInput
                                                        register={register}
                                                        name={'invoice_street'}
                                                        translationIndex={t('branches.invoice_street')}
                                                        errors={errors}
                                                    />
                                                </div>
                                                <div className="mb-2">
                                                    <TextInput
                                                        register={register}
                                                        name={'invoice_number'}
                                                        translationIndex={t('branches.invoice_number')}
                                                        errors={errors}
                                                    />
                                                </div>
                                                <div className="mb-2">
                                                    <TextInput
                                                        register={register}
                                                        name={'invoice_bus'}
                                                        translationIndex={t('branches.invoice_bus')}
                                                        errors={errors}
                                                    />
                                                </div>
                                                <div className="mb-2">
                                                    <TextInput
                                                        register={register}
                                                        name={'invoice_postal'}
                                                        translationIndex={t('branches.invoice_postal')}
                                                        errors={errors}
                                                    />
                                                </div>
                                                <div className="mb-2">
                                                    <TextInput
                                                        register={register}
                                                        name={'invoice_city'}
                                                        translationIndex={t('branches.invoice_city')}
                                                        errors={errors}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        <div className="mb-4 p-3 bg-gray-100 rounded-lg border border-gray-200">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-medium text-gray-900">
                                                    {t('branches.has_different_contract_destination')}
                                                </span>
                                                <Switch
                                                    checked={hasContractsDestination}
                                                    onChange={setHasContractsDestination}
                                                    className={`${
                                                        hasContractsDestination ? 'bg-primary-600' : 'bg-gray-300'
                                                    } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
                                                >
                                                    <span
                                                        className={`${
                                                            hasContractsDestination ? 'translate-x-6' : 'translate-x-1'
                                                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                                                    />
                                                </Switch>
                                            </div>
                                        </div>

                                        {hasContractsDestination && (
                                            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                                <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('branches.contract_destination')}</h3>

                                                <div className="mb-2">
                                                    <TextInput
                                                        register={register}
                                                        name={'contracts_email'}
                                                        translationIndex={t('branches.contract_email')}
                                                        errors={errors}
                                                    />
                                                </div>
                                                <div className="mb-2">
                                                    <TextInput
                                                        register={register}
                                                        name={'contracts_firstname'}
                                                        translationIndex={t('branches.contract_firstname')}
                                                        errors={errors}
                                                    />
                                                </div>
                                                <div className="mb-2">
                                                    <TextInput
                                                        register={register}
                                                        name={'contracts_lastname'}
                                                        translationIndex={t('branches.contract_lastname')}
                                                        errors={errors}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        <div className="mb-4 p-3 bg-gray-100 rounded-lg border border-gray-200">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-medium text-gray-900">
                                                    {t('branches.has_different_performance_statement_destination')}
                                                </span>
                                                <Switch
                                                    checked={hasPerformanceStatementDestination}
                                                    onChange={setHasPerformanceStatementDestination}
                                                    className={`${
                                                        hasPerformanceStatementDestination ? 'bg-primary-600' : 'bg-gray-300'
                                                    } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
                                                >
                                                    <span
                                                        className={`${
                                                            hasPerformanceStatementDestination ? 'translate-x-6' : 'translate-x-1'
                                                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                                                    />
                                                </Switch>
                                            </div>
                                        </div>

                                        {hasPerformanceStatementDestination && (
                                            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                                <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('branches.performance_statement_destination')}</h3>

                                                <div className="mb-2">
                                                    <TextInput
                                                        register={register}
                                                        name={'performancestatement_email'}
                                                        translationIndex={t('branches.performance_email')}
                                                        errors={errors}
                                                    />
                                                </div>
                                                <div className="mb-2">
                                                    <TextInput
                                                        register={register}
                                                        name={'performancestatement_firstname'}
                                                        translationIndex={t('branches.performance_firstname')}
                                                        errors={errors}
                                                    />
                                                </div>
                                                <div className="mb-2">
                                                    <TextInput
                                                        register={register}
                                                        name={'performancestatement_lastname'}
                                                        translationIndex={t('branches.performance_lastname')}
                                                        errors={errors}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {
                                            user?.roles?.includes('admin') && (
                                                <>
                                                    <div className="mb-2">
                                                        <TextInput
                                                            register={register}
                                                            name={'relation_code_exact'}
                                                            translationIndex={'general.fields.relation_code_exact'}
                                                            errors={errors}
                                                        />
                                                    </div>

                                                    <div className="mb-2">
                                                        <SelectInput
                                                            name={'contract_type_id'}
                                                            options={contractTypes.map((item) => {
                                                                return {
                                                                    id: item.id,
                                                                    value: item.id,
                                                                    label: item.name
                                                                };
                                                            })}
                                                            setValue={setValue}
                                                            control={control}
                                                            value={getValues('contract_type_id') ?? null}
                                                            placeholder={t('general.fields.province')}
                                                            translationIndex={t('branches.contract_type')}
                                                        />
                                                    </div>
                                                </>
                                            )
                                        }

                                        <div className="mb-2">
                                            <NumberInput
                                                register={register}
                                                name={'max_hourly_rate_zk'}
                                                step={0.01}
                                                translationIndex={t('branches.max_rate_caregivers')}
                                                errors={errors}
                                            />
                                            {
                                                user?.roles?.includes('admin') && !isMyCcpSubdomain() && (
                                                    <p className={'text-sm text-gray-500 mt-1'}><strong>{t('registrations.external_rate')}:</strong> {watch('max_hourly_rate_zk') ? (Math.round(watch('max_hourly_rate_zk') * 1.18 * 100) / 100).toFixed(2).replace('.', ',') : '-'}</p>
                                            )}
                                        </div>

                                        <div className="mb-2">
                                            <NumberInput
                                                register={register}
                                                name={'max_hourly_rate_vk'}
                                                step={0.01}
                                                translationIndex={t('branches.max_rate_nurses')}
                                                errors={errors}
                                            />
                                            {
                                                user?.roles?.includes('admin') && !isMyCcpSubdomain() && (
                                                    <p className={'text-sm text-gray-500 mt-1'}><strong>{t('registrations.external_rate')}:</strong> {watch('max_hourly_rate_vk') ? (Math.round(watch('max_hourly_rate_vk') * 1.18 * 100) / 100).toFixed(2).replace('.', ',') : '-'}</p>
                                            )}
                                        </div>

                                        <PrimaryButton onClick={handleSubmit(onSubmit)} className={'mt-4 w-full'}>
                                            {t('branches.update_branch')}
                                        </PrimaryButton>
                                    </form>
                                </div>
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};
